import { useCallback } from 'react';
import { Stack, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import QueryKeys from '@constants/queryKeys';
import InboxSkeletonLoader from '@components/InboxReviews/InboxSkeletonLoader';
import OtherNotificationsTable from '@components/InboxReviews/Tables/OtherNotificationsTable';
import ThPagination from '@components/ThPagination/ThPagination';
import { useWorkerOtherNotifications } from '@worker/hooks/useWorkerOtherNotifications';
import { useDismissNotification } from '@worker/hooks/useDismissNotification';
import { useMarkOtherNotificationRead } from '@worker/hooks/useMarkOtherNotificationRead';
import { useWorkerInboxQueryState } from '@worker/hooks/useWorkerInboxQueryState';
import type { OtherNotificationItem } from '@th-types/worker-notifications.type';
import { handleInboxPageChange } from '@utils/InboxHelper';
import { DEFAULT_ITEMS_PER_PAGE } from '@utils/Pagination';

export default function OtherTab() {
  const { searchParams, setSearchParams, urlParams } =
    useWorkerInboxQueryState();
  const queryClient = useQueryClient();
  const dismissMutation = useDismissNotification();
  const markReadMutation = useMarkOtherNotificationRead();
  const currentOffset = urlParams.offset ?? 0;

  const { data, isLoading } = useWorkerOtherNotifications({
    offset: currentOffset,
    max: DEFAULT_ITEMS_PER_PAGE,
  });

  const handleOpen = useCallback(
    async (notification: OtherNotificationItem) => {
      // Await the mutation: window.location.href unloads the page and would
      // cancel an in-flight request, losing the server-side read state.
      if (!notification.isRead) {
        try {
          await markReadMutation.mutateAsync(notification.id);
        } catch {
          // onError already shows a toast; swallow so navigation still runs.
        }
      }
      if (notification.actionUrl) {
        // Skip invalidation: the full-page navigation would abort the refetch
        // and flash a misleading "Could not load notifications." toast.
        window.location.href = notification.actionUrl;
        return;
      }
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.WorkerInbox.OTHER_NOTIFICATIONS],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.WorkerInbox.INDICATORS],
      });
    },
    [markReadMutation, queryClient]
  );

  const handleDismiss = useCallback(
    (id: number) => dismissMutation.mutate(id),
    [dismissMutation]
  );

  const notifications = data?.list ?? [];
  const totalCount = data?.count ?? 0;

  return (
    <Stack gap="md" w="100%" p={{ base: 'xs', sm: 'md' }}>
      {isLoading && <InboxSkeletonLoader />}

      {!isLoading && notifications.length === 0 && (
        <Text c="dimmed" ta="center" py="xl">
          No notifications.
        </Text>
      )}

      {!isLoading && notifications.length > 0 && (
        <>
          <OtherNotificationsTable
            notifications={notifications}
            onOpen={handleOpen}
            onDismiss={handleDismiss}
          />
          {totalCount > DEFAULT_ITEMS_PER_PAGE && (
            <ThPagination
              currentOffset={currentOffset}
              itemsPerPage={DEFAULT_ITEMS_PER_PAGE}
              totalItems={totalCount}
              onPageChange={(offset) =>
                handleInboxPageChange(offset, searchParams, setSearchParams)
              }
            />
          )}
        </>
      )}
    </Stack>
  );
}
